import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const MemberCard = ({ member, user, isCurrentUser = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div
      className={`w-full p-6 rounded-xl border-2 transition-all duration-200 ${
        isCurrentUser 
          ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 shadow-lg' 
          : 'bg-white border-gray-200 hover:bg-gray-50 hover:shadow-md'
      }`}
    >
      <div className="flex items-center space-x-4">
        {/* Profile Image */}
        <div className="relative">
          {member.user?.profilePicture ? (
            <img
              src={member.user.profilePicture}
              alt={member.user.fullName || `${member.user.firstName} ${member.user.lastName}`}
              className={`w-16 h-16 rounded-full object-cover border-4 ${
                isCurrentUser ? 'border-orange-400' : 'border-gray-200'
              }`}
            />
          ) : (
            <div className={`w-16 h-16 rounded-full bg-gray-300 flex items-center justify-center border-4 ${
              isCurrentUser ? 'border-orange-400' : 'border-gray-200'
            }`}>
              <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          )}
          
        </div>

        {/* Member Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-2">
            <h4 className="text-lg font-bold text-gray-900 truncate">
              {member.user?.fullName || `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim() || member.user?.userName || 'Unknown'}
            </h4>
            {isCurrentUser && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-200 text-orange-900">
                {t("common.you")}
              </span>
            )}
          </div>
          
          <p className="text-base text-gray-600 mb-3">
            @{member.user?.userName || 'username'}
          </p>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 capitalize bg-gray-100 px-3 py-1 rounded-full">
              {member.accountType}
            </span>
            
            {/* Attendance Status */}
            <div className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${
              member.attendanceStatus === 'coming' 
                ? 'bg-green-100 text-green-800'
                : member.attendanceStatus === 'notComing'
                ? 'bg-red-100 text-red-800' 
                : member.attendanceStatus === 'doNotKnow'
                ? 'bg-yellow-100 text-yellow-800'
                : member.attendanceStatus === 'attended'
                ? 'bg-blue-100 text-blue-800'
                : member.attendanceStatus === 'absent'
                ? 'bg-gray-100 text-gray-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-2 ${
                member.attendanceStatus === 'coming' 
                  ? 'bg-green-500'
                  : member.attendanceStatus === 'notComing'
                  ? 'bg-red-500' 
                  : member.attendanceStatus === 'doNotKnow'
                  ? 'bg-yellow-500'
                  : member.attendanceStatus === 'attended'
                  ? 'bg-blue-500'
                  : member.attendanceStatus === 'absent'
                  ? 'bg-gray-500'
                  : 'bg-gray-500'
              }`}></div>
              {member.attendanceStatus === 'coming' && t("player.going")}
              {member.attendanceStatus === 'notComing' && t("player.notGoing")}
              {member.attendanceStatus === 'doNotKnow' && t("player.maybe")}
              {member.attendanceStatus === 'attended' && t("player.attended")}
              {member.attendanceStatus === 'absent' && t("player.absent")}
              {member.attendanceStatus === 'pending' && t("player.pending")}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons - Horizontal layout at bottom */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        {!isCurrentUser && (
          <div className="flex space-x-3">
            <button
              onClick={() => navigate(`/profile/${member.userId}`)}
              className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              {t("player.viewFeed")}
            </button>
            
            <button
              onClick={() => navigate(`/chat/${member.userId}`)}
              className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {t("player.startChat")}
            </button>
          </div>
        )}

        {/* Current User - Show only profile actions, no chat to self */}
        {isCurrentUser && (
          <div className="flex justify-center">
            <button
              onClick={() => navigate(`/feed/${member.userId}`)}
              className="inline-flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              {t("player.myFeed")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberCard; 